import React, { useState, useEffect } from 'react';
import moment from 'moment-timezone';
import { format } from 'date-fns';
import { ApolloError } from 'apollo-client';

import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import Buttons from 'components/Buttons';
import CoachingConstants, { CoachingSessionDuration } from 'coaching/CoachingConstants';
import DateTimeAvailabilitySelect from 'components/DateTimeAvailabilitySelect';
import Divider from 'components/Divider';
import EventTracking from 'lib/EventTracking';
import Icon from 'components/Icon';
import LoadingIndicator from 'components/LoadingIndicator';
import NoCoachAvailabilityNullState from 'coaching/components/CoachingHomeView/NoCoachAvailabilityNullState';
import Row, { RowSize } from 'components/Row';
import Text, { TextSize, TextColor, TextWeight } from 'components/Text';
import { DurationSelectRow } from 'coaching/components/CoachingHomeView/BookSessionSelectRows';
import { getDatesFromReservations } from 'coaching/lib/timeIntervals';
import {
  CoachingSessionSlot,
  CoachingTimeOff,
} from 'coaching/containers/GetPossibleCoachingSessionsForCoachContainer';
import { CoachingEventDefinitionInput } from 'globalTypes';
import { SelfScheduleOptions } from 'coaching/components/CoachingHomeView/BookNewCoachingSessionSidebar';

export type Props = {
  startDateTimeRange: string;
  endDateTimeRange: string;
  weekOffset: number;
  timezone: string;
  sessionSlots?: CoachingSessionSlot[];
  timeOff?: CoachingTimeOff[];
  onSelectReservation: (availableSchedule?: {
    eventDefinition: CoachingEventDefinitionInput;
    selectedAvailability: CoachingSessionSlot;
  }) => void;
  onWeekForward: () => void;
  onWeekBack: () => void;
  selfScheduleOptions: SelfScheduleOptions;
  eventPrefix?: string;
  loading: boolean;
  error?: ApolloError;
};

export default function BookOneOffSession<T>(props: Props) {
  const {
    startDateTimeRange,
    endDateTimeRange,
    weekOffset,
    timezone,
    sessionSlots,
    onSelectReservation,
    onWeekBack,
    onWeekForward,
    eventPrefix,
    loading,
    error,
    selfScheduleOptions,
  } = props;
  function trackKey(key: string, properties?: Record<string, any>) {
    eventPrefix && EventTracking.trackKey(`${eventPrefix}.${key}`, properties);
  }
  function trackShowAvailabilityToggle() {
    trackKey('show_availability.toggle');
  }

  const [selectedOption, setSelectedOption] = useState<CoachingSessionSlot>();
  const [selectedDuration, setSelectedDuration] = useState<CoachingSessionDuration>(
    props.selfScheduleOptions.durationOptions[0]
  );

  useEffect(() => {
    trackKey('view');
  }, []);

  useEffect(() => {
    onSelectReservation(undefined);
  }, [selectedOption]);

  useEffect(() => {
    // if the slot just got unselected, no need to do anything else
    if (!selectedOption) {
      return;
    }

    const proposedEventDefinition = generateEventDefinition(
      selectedDuration,
      selectedOption.startDateTime,
      timezone
    );
    onSelectReservation({
      eventDefinition: proposedEventDefinition,
      selectedAvailability: selectedOption,
    });
  }, [selectedOption, selectedDuration, timezone]);

  if (loading) {
    return (
      <BoxBody size="small">
        <Text align="center">
          <LoadingIndicator data-test="loading-indicator" />
        </Text>
      </BoxBody>
    );
  }

  if (error || !sessionSlots) {
    return <NoCoachAvailabilityNullState />;
  }

  const daysToShow = getDatesFromReservations(sessionSlots.map(o => o.startDateTime));

  return (
    <>
      <BoxBody size="small">
        <WeekTraversalHeader
          onWeekBack={() => {
            setSelectedOption(undefined);
            onWeekBack();
            trackKey('previous_week.click');
          }}
          onWeekForward={() => {
            setSelectedOption(undefined);
            onWeekForward();
            trackKey('next_week.click');
          }}
          weekOffset={weekOffset}
          maxOffset={4}
          startDateTimeRange={startDateTimeRange}
          endDateTimeRange={endDateTimeRange}
        />
        <DateTimeAvailabilitySelect
          daysPerRow={4}
          slotsPerRow={2}
          daysToShow={daysToShow}
          dateTimeOptions={sessionSlots}
          onChange={option => {
            setSelectedOption(option);
            trackKey('time.click', { data: option.startDateTime.toISOString() });
          }}
          value={selectedOption}
          onSelectDay={day => {
            setSelectedOption(undefined);
            trackKey('date.click', { data: day.format('YYYY-MM-DD') });
          }}
          emptyText={<NoCoachAvailabilityNullState />}
          onMoreAvailabilities={trackShowAvailabilityToggle}
          onLessAvailabilities={trackShowAvailabilityToggle}
        />
        <Divider />
        {selfScheduleOptions.durationOptions.length > 1 && (
          <DurationSelectRow
            durationOptions={selfScheduleOptions.durationOptions}
            selectedDuration={selectedDuration}
            onSelectDuration={duration => {
              setSelectedDuration(duration);
              trackKey('length.click');
            }}
          />
        )}
      </BoxBody>
    </>
  );
}

export function WeekTraversalHeader({
  onWeekBack,
  onWeekForward,
  startDateTimeRange,
  endDateTimeRange,
  weekOffset,
  maxOffset,
  buttonSize = 'xxxsmall',
  timeRangeTextSize = 'xxsmall',
  size = 'small',
  dateRangeTextColor = 'graphite',
  dateRangeTextWeight = 'normal',
}: {
  startDateTimeRange: string;
  endDateTimeRange: string;
  weekOffset: number;
  maxOffset?: number;
  onWeekForward: () => void;
  onWeekBack: () => void;
  buttonSize?: TextSize;
  timeRangeTextSize?: TextSize;
  size?: RowSize;
  dateRangeTextColor?: TextColor;
  dateRangeTextWeight?: TextWeight;
}) {
  return (
    <Row size={size}>
      <Buttons fullButtonIndex={1} reset="none">
        <Button
          data-test="week-offset-back-arrow"
          level="icon"
          color="graphite"
          onClick={onWeekBack}
          disabled={weekOffset <= 0}>
          <Text size={buttonSize} align="left">
            <Icon size="inherit" name="chevron-left" />
          </Text>
        </Button>
        <span tabIndex={0}>
          <Text
            data-test="week-time-range-text"
            size={timeRangeTextSize}
            color={dateRangeTextColor}
            weight={dateRangeTextWeight}
            align="center">
            {format(startDateTimeRange, 'MMM D')} - {format(endDateTimeRange, 'MMM D')}
          </Text>
        </span>
        <Button
          data-test="week-offset-forward-arrow"
          level="icon"
          color="graphite"
          onClick={onWeekForward}
          disabled={maxOffset !== undefined ? weekOffset >= maxOffset : false}>
          <Text size={buttonSize} align="right">
            <Icon size="inherit" name="chevron-right" />
          </Text>
        </Button>
      </Buttons>
    </Row>
  );
}

function generateEventDefinition(
  duration: CoachingSessionDuration,
  startDateTime: moment.Moment,
  timezone: string
) {
  return {
    startDateTime: startDateTime.toISOString(),
    endDateTime: moment
      .utc(startDateTime)
      .minutes(duration)
      .toISOString(),
    recurrence: [],
    timezone,
  };
}
